import React from 'react';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import { Checkbox } from '../../../components/ui/Checkbox';

const ProgramSelectionStep = ({ formData, errors, onChange }) => {
  const institutionOptions = [
    { value: 'peking', label: 'Peking University' },
    { value: 'tsinghua', label: 'Tsinghua University' },
    { value: 'fudan', label: 'Fudan University' },
    { value: 'shanghai-jiao', label: 'Shanghai Jiao Tong University' },
    { value: 'zhejiang', label: 'Zhejiang University' },
    { value: 'nanjing', label: 'Nanjing University' },
    { value: 'wuhan', label: 'Wuhan University' },
    { value: 'beijing-normal', label: 'Beijing Normal University' }
  ];

  const programTypeOptions = [
    { value: 'intensive', label: 'Intensive Chinese Language Program', description: 'Full-time immersive language study' },
    { value: 'standard', label: 'Standard Chinese Language Program', description: 'Regular pace language courses' },
    { value: 'preparatory', label: 'University Preparatory Program', description: 'Preparation for degree programs' },
    { value: 'business', label: 'Business Chinese Program', description: 'Chinese for professional contexts' },
    { value: 'hsk-prep', label: 'HSK Test Preparation', description: 'Focused HSK exam preparation' }
  ];

  const durationOptions = [
    { value: '1-semester', label: '1 Semester (4-5 months)' },
    { value: '2-semester', label: '2 Semesters (8-10 months)' },
    { value: '1-year', label: '1 Academic Year (10-12 months)' },
    { value: '2-year', label: '2 Academic Years (20-24 months)' }
  ];

  const intakeOptions = [
    { value: 'spring-2026', label: 'Spring 2026 (March)' },
    { value: 'fall-2026', label: 'Fall 2026 (September)' },
    { value: 'spring-2027', label: 'Spring 2027 (March)' },
    { value: 'fall-2027', label: 'Fall 2027 (September)' }
  ];

  const accommodationOptions = [
    { value: 'on-campus-single', label: 'On-Campus Single Room', description: 'Private room in university dormitory' },
    { value: 'on-campus-shared', label: 'On-Campus Shared Room', description: 'Shared room with another student' },
    { value: 'off-campus', label: 'Off-Campus Housing', description: 'I will arrange my own accommodation' },
    { value: 'homestay', label: 'Homestay Program', description: 'Live with a local Chinese family' }
  ];

  const currencyOptions = [
    { value: 'usd', label: 'USD ($)' },
    { value: 'eur', label: 'EUR (€)' },
    { value: 'cny', label: 'CNY (¥)' }
  ];

  const calculateFees = () => {
    const baseFee = 5000;
    const currency = formData?.preferredCurrency || 'usd';
    const rates = { usd: 1, eur: 0.85, cny: 6.5 };
    return (baseFee * rates?.[currency])?.toFixed(2);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground mb-2">
          Program Selection
        </h2>
        <p className="text-sm md:text-base text-muted-foreground font-caption">
          Choose your preferred institution, program type, and study duration.
        </p>
      </div>
      <div className="space-y-4 md:space-y-6">
        <Select
          label="Preferred Institution"
          options={institutionOptions}
          value={formData?.institution || ''}
          onChange={(value) => onChange('institution', value)}
          error={errors?.institution}
          placeholder="Select institution"
          searchable
          required
        />

        <Select
          label="Program Type"
          options={programTypeOptions}
          value={formData?.programType || ''}
          onChange={(value) => onChange('programType', value)}
          error={errors?.programType}
          placeholder="Select program type"
          required
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          <Select
            label="Study Duration"
            options={durationOptions}
            value={formData?.duration || ''}
            onChange={(value) => onChange('duration', value)}
            error={errors?.duration}
            placeholder="Select duration"
            required
          />

          <Select
            label="Preferred Intake"
            options={intakeOptions}
            value={formData?.intake || ''}
            onChange={(value) => onChange('intake', value)}
            error={errors?.intake}
            placeholder="Select intake period"
            description="Application deadline: 3 months before intake"
            required
          />
        </div>

        <div className="p-4 md:p-6 bg-accent/10 rounded-lg border border-accent/20">
          <div className="flex items-start gap-3">
            <Icon name="Calendar" size={20} color="var(--color-accent)" />
            <div>
              <p className="text-sm md:text-base font-medium text-foreground mb-1">
                Application Deadline
              </p>
              <p className="text-xs md:text-sm text-muted-foreground font-caption">
                {formData?.intake === 'spring-2026' && 'December 31, 2025'}
                {formData?.intake === 'fall-2026' && 'June 30, 2026'}
                {formData?.intake === 'spring-2027' && 'December 31, 2026'}
                {formData?.intake === 'fall-2027' && 'June 30, 2027'}
                {!formData?.intake && 'Select intake to view deadline'}
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="pt-4 border-t border-border">
        <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground mb-4">
          Accommodation Preferences
        </h3>

        <Select
          label="Accommodation Type"
          options={accommodationOptions}
          value={formData?.accommodation || ''}
          onChange={(value) => onChange('accommodation', value)}
          error={errors?.accommodation}
          placeholder="Select accommodation preference"
          required
        />

        <div className="mt-4">
          <Checkbox
            label="I require airport pickup service"
            description="Additional fee may apply"
            checked={formData?.airportPickup || false}
            onChange={(e) => onChange('airportPickup', e?.target?.checked)}
          />
        </div>
      </div>
      <div className="pt-4 border-t border-border">
        <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground mb-4">
          Additional Preferences
        </h3>

        <div className="space-y-4">
          <Checkbox
            label="I am interested in cultural activities and excursions"
            checked={formData?.culturalActivities || false}
            onChange={(e) => onChange('culturalActivities', e?.target?.checked)}
          />

          <Checkbox
            label="I would like to participate in language exchange programs"
            checked={formData?.languageExchange || false}
            onChange={(e) => onChange('languageExchange', e?.target?.checked)}
          />

          <Checkbox
            label="I am interested in part-time work opportunities (if eligible)"
            checked={formData?.partTimeWork || false}
            onChange={(e) => onChange('partTimeWork', e?.target?.checked)}
          />
        </div>
      </div>
      <div className="pt-4 border-t border-border">
        <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground mb-4">
          Fee Calculation
        </h3>

        <div className="space-y-4 md:space-y-6">
          <Select
            label="Preferred Currency"
            options={currencyOptions}
            value={formData?.preferredCurrency || 'usd'}
            onChange={(value) => onChange('preferredCurrency', value)}
            placeholder="Select currency"
          />

          <div className="p-4 md:p-6 bg-primary/5 rounded-lg border border-primary/20">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm md:text-base font-caption font-medium text-foreground">
                Estimated Program Fee
              </span>
              <span className="text-xl md:text-2xl font-heading font-semibold text-primary data-text">
                {formData?.preferredCurrency === 'eur' && '€'}
                {formData?.preferredCurrency === 'cny' && '¥'}
                {formData?.preferredCurrency === 'usd' && '$'}
                {calculateFees()}
              </span>
            </div>
            <p className="text-xs md:text-sm text-muted-foreground font-caption">
              * Final fees may vary based on program and accommodation selection
            </p>
          </div>
        </div>
      </div>
      <Input
        label="Special Requirements or Comments"
        type="text"
        placeholder="Any special needs or additional information"
        value={formData?.specialRequirements || ''}
        onChange={(e) => onChange('specialRequirements', e?.target?.value)}
        description="Optional: dietary restrictions, medical needs, etc."
      />
    </div>
  );
};

export default ProgramSelectionStep;